import React, { Component } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { colorSchemeLight, themeQuartz } from 'ag-grid-community';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Checkbox,
    FormLabel,
    InputAdornment,
    IconButton
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import toastr from 'toastr';
import { fetchFacilitiesForOrders } from '../../../services/clinicPortalServices/facilityServices';
import { getAllSamplesData } from '../../../services/clinicPortalServices/allSamplesService';
import { getOrderDataById } from '../../../services/clinicPortalServices/orderEditService';
import OrderViewPopup, { SubOrderDrawer } from '../orders/clinicOrderGrid/orderViewPopup';
import Breadcrumb from './breadcrumb';

const agTheme = themeQuartz.withPart(colorSchemeLight);

const SAMPLE_STATUSES = ['received', 'pending', 'rejected', 'processing', 'completed', 'cancelled'];

const STATUS_COLORS = {
    received: '#198754',
    pending: '#e6a817',
    rejected: '#dc3545',
    processing: '#0d6efd',
    completed: '#6f42c1',
    cancelled: '#6c757d',
};

class AllSamplesGrid extends Component {
    constructor(props) {
        super(props);
        this.state = {
            rowData: [],
            loading: false,
            facilities: [],
            facilityId: [],
            searchFilters: {
                facility_id: [],
                from_date: null,
                to_date: null,
                dateRange: null,
                selectedStatuses: [],
            },
            quickFilterText: '',
            paginationPageSize: 50,
            pickerOpened: false,
            showOrderViewPopup: false,
            orderViewDetails: null,
            showDrawer: false,
            drawerOrderDetails: null,
        };

        this.columnDefs = [
            {
                headerName: 'Order ID',
                field: 'lab_order_id',
                sortable: true,
                filter: true,
                width: 130,
                pinned: 'left',
            },
            {
                headerName: 'Actions',
                field: '_id',
                width: 90,
                sortable: false,
                filter: false,
                cellRenderer: (params) => {
                    return (
                        <div className="d-flex align-items-center gap-1">
                            <button
                                className="btn btn-sm btn-outline-secondary py-0 px-2"
                                title="View Order"
                                onClick={() => params.context.openViewPopup(params.data)}
                            >
                                <i className="fas fa-folder-open"></i>
                            </button>
                        </div>
                    );
                },
            },
            { headerName: 'Sample ID', field: 'sample_id', sortable: true, filter: true, width: 160 },
            { headerName: 'Patient', field: 'patient_name', sortable: true, filter: true, width: 180 },
            { headerName: 'DOB', field: 'dob', sortable: true, filter: true, width: 110 },
            { headerName: 'Sample Type', field: 'sample_type', sortable: true, filter: true, width: 140 },
            { headerName: 'Tube Type', field: 'tube_type', sortable: true, filter: true, width: 130 },
            {
                headerName: 'Status',
                field: 'sample_status',
                sortable: true,
                filter: true,
                width: 130,
                cellRenderer: (params) => {
                    if (!params.value) {
                        return <span style={{ color: '#aaa' }}>—</span>;
                    }
                    const color = STATUS_COLORS[params.value.toLowerCase()] || '#6c757d';
                    return (
                        <span
                            className="badge"
                            style={{ backgroundColor: color, fontSize: '12px', padding: '4px 8px' }}
                        >
                            {params.value}
                        </span>
                    );
                },
            },
            {
                headerName: 'Collected Date',
                field: 'collected_date',
                sortable: true,
                filter: true,
                width: 160,
                cellRenderer: (params) => {
                    if (!params.value) return <span style={{ color: '#aaa' }}>—</span>;
                    const m = moment(params.value, 'YYYYMMDDHHmmss');
                    return <span>{m.isValid() ? m.format('MM/DD/YYYY HH:mm') : params.value}</span>;
                },
            },
            {
                headerName: 'Received Date',
                field: 'received_date',
                sortable: true,
                filter: true,
                width: 160,
                cellRenderer: (params) => {
                    if (!params.value) return <span style={{ color: '#aaa' }}>—</span>;
                    const m = moment(params.value, 'YYYYMMDDHHmmss');
                    return <span>{m.isValid() ? m.format('MM/DD/YYYY HH:mm') : params.value}</span>;
                },
            },
            { headerName: 'Collector', field: 'collector_name', sortable: true, filter: true, width: 160 },
            { headerName: 'Facility', field: 'facility_name', sortable: true, filter: true, width: 200 },
            { headerName: 'Physician', field: 'physician_name', sortable: true, filter: true, width: 180 },
            {
                headerName: 'Notes',
                field: 'notes',
                sortable: false,
                filter: true,
                flex: 1,
                minWidth: 200,
                cellRenderer: (params) => {
                    if (!params.value) {
                        return <span style={{ color: '#aaa' }}>—</span>;
                    }
                    return <span title={params.value}>{params.value}</span>;
                },
            },
        ];

        this.defaultColDef = { resizable: true };
    }

    componentDidMount() {
        fetchFacilitiesForOrders().then((res) => {
            const facilities = res.data || res || [];
            const allIds = facilities.map((f) => f._id);
            this.setState(
                {
                    facilities,
                    facilityId: allIds,
                    searchFilters: { ...this.state.searchFilters, facility_id: allIds },
                },
                () => this.loadData()
            );
        });
    }

    loadData = () => {
        const { searchFilters } = this.state;
        this.setState({ loading: true });
        const params = {
            facility_id: searchFilters.facility_id,
            ...(searchFilters.from_date ? { from_date: searchFilters.from_date } : {}),
            ...(searchFilters.to_date ? { to_date: searchFilters.to_date } : {}),
        };
        getAllSamplesData(params)
            .then((res) => {
                let rows = res.data || [];
                const { selectedStatuses } = searchFilters;
                if (selectedStatuses && selectedStatuses.length > 0) {
                    rows = rows.filter((r) => selectedStatuses.includes((r.sample_status || '').toLowerCase()));
                }
                this.setState({ rowData: rows, loading: false });
            })
            .catch(() => {
                this.setState({ loading: false });
                toastr.error('Failed to load samples.');
            });
    };

    handleFacilityChange = (e) => {
        const { facilities } = this.state;
        let value = e.target.value;
        if (value.includes('all')) {
            const allIds = facilities.map((f) => f._id);
            value = this.state.facilityId.length === allIds.length ? [] : allIds;
        }
        this.setState({
            facilityId: value,
            searchFilters: { ...this.state.searchFilters, facility_id: value },
        });
    };

    handleStatusChange = (e) => {
        let value = e.target.value;
        if (value.includes('all')) {
            value = this.state.searchFilters.selectedStatuses.length === SAMPLE_STATUSES.length ? [] : [...SAMPLE_STATUSES];
        }
        this.setState({ searchFilters: { ...this.state.searchFilters, selectedStatuses: value } });
    };

    updateDateRange = (range) => {
        const fmt = (m) => m.format('YYYY-MM-DD') + 'T00:00';
        let from_date, to_date;
        if (range === 'today') {
            from_date = fmt(moment()); to_date = fmt(moment().add(1, 'days'));
        } else if (range === 'week') {
            from_date = fmt(moment().startOf('week')); to_date = fmt(moment().endOf('week').add(1, 'days'));
        } else if (range === 'month') {
            from_date = fmt(moment().startOf('month')); to_date = fmt(moment().endOf('month').add(1, 'days'));
        } else if (range === 'quarter') {
            from_date = fmt(moment().startOf('quarter')); to_date = fmt(moment().endOf('quarter').add(1, 'days'));
        }
        this.setState(
            { searchFilters: { ...this.state.searchFilters, from_date, to_date, dateRange: range } },
            () => this.loadData()
        );
    };

    handleDateChange = (dateVal, field) => {
        const formatted = moment(dateVal).format('YYYY-MM-DD') + 'T' + moment(dateVal).format('HH:mm');
        this.setState({
            searchFilters: { ...this.state.searchFilters, [field]: formatted, dateRange: 'custom' },
        });
    };

    applyFilters = () => this.loadData();

    clearFilters = () => {
        if (this.gridApi) this.gridApi.setFilterModel(null);
        this.setState({ quickFilterText: '' });
        const el = document.getElementById('as-grid-search');
        if (el) el.value = '';
    };

    onFilterTextChange = (e) => {
        const val = e.target.value;
        this.setState({ quickFilterText: val });
        if (this.gridApi) this.gridApi.setQuickFilter(val);
    };

    onPageSizeChanged = (e) => {
        const size = parseInt(e.target.value, 10);
        if (!isNaN(size) && size > 0) {
            this.setState({ paginationPageSize: size });
            if (this.gridApi) this.gridApi.paginationSetPageSize(size);
        }
    };

    onGridReady = (params) => { this.gridApi = params.api; };

    openViewPopup = (rowData) => {
        const orderId = rowData && rowData.order_id;
        if (!orderId) return;
        getOrderDataById(orderId).then((res) => {
            if (res && res.data) {
                this.setState({ orderViewDetails: res.data, showOrderViewPopup: true });
            }
        }).catch(() => toastr.error('Could not load order details.'));
    };

    openDrawer = (rowData) => {
        const orderId = rowData && rowData.order_id;
        if (!orderId) return;
        getOrderDataById(orderId).then((res) => {
            if (res && res.data) {
                this.setState({ drawerOrderDetails: res.data, showDrawer: true });
            }
        }).catch(() => {});
    };

    onRowClicked = (params) => {
        if (params.column && params.column.colId === '_id') return;
        this.openViewPopup(params.data);
    };

    render() {
        const {
            rowData, loading, facilities, facilityId, searchFilters,
            paginationPageSize, quickFilterText, pickerOpened,
            showOrderViewPopup, orderViewDetails,
        } = this.state;
        const { from_date, to_date, dateRange, selectedStatuses } = searchFilters;

        return (
            <div className="clinicPortal">
                <div className="clinicWorkSpace">
                    <Breadcrumb />

                    {/* ── Filter Bar ── */}
                    <div className="row mt-4 px-3">
                        <div className="col-md-9 col-12" style={{ borderRight: '2px solid lightGray' }}>
                            <div className="row">
                                {/* Facility + Status */}
                                <div className="col-md-6 col-12 mb-3">
                                    <FormControl className="filter-select-bar" variant="outlined">
                                        <InputLabel id="as-facility-label">Select Facility</InputLabel>
                                        <Select
                                            labelId="as-facility-label"
                                            value={facilityId}
                                            onChange={this.handleFacilityChange}
                                            label="Select Facility"
                                            multiple
                                            renderValue={(selected) => {
                                                if (selected.length === facilities.length && facilities.length > 0) return 'All Facilities';
                                                return facilities.filter((f) => selected.includes(f._id)).map((f) => f.name).join(', ');
                                            }}
                                        >
                                            <MenuItem value="all" key="all" className={facilityId.length === facilities.length ? 'Mui-selected' : ''}>
                                                <Checkbox checked={facilityId.length === facilities.length && facilities.length > 0} />
                                                All Facilities
                                            </MenuItem>
                                            {facilities.map((fac) => (
                                                <MenuItem key={fac._id} value={fac._id}>
                                                    <Checkbox checked={facilityId.includes(fac._id)} />
                                                    {fac.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <FormControl className="filter-select-bar mt-2" variant="outlined">
                                        <InputLabel id="as-status-label">Select Status</InputLabel>
                                        <Select
                                            labelId="as-status-label"
                                            value={selectedStatuses}
                                            onChange={this.handleStatusChange}
                                            label="Select Status"
                                            multiple
                                            renderValue={(selected) => {
                                                if (selected.length === 0 || selected.length === SAMPLE_STATUSES.length) return 'All';
                                                return selected.join(', ');
                                            }}
                                        >
                                            <MenuItem value="all" key="all" className={selectedStatuses.length === SAMPLE_STATUSES.length ? 'Mui-selected' : ''}>
                                                <Checkbox checked={selectedStatuses.length === SAMPLE_STATUSES.length} />
                                                All
                                            </MenuItem>
                                            {SAMPLE_STATUSES.map((s) => (
                                                <MenuItem key={s} value={s}>
                                                    <Checkbox checked={selectedStatuses.includes(s)} />
                                                    {s.charAt(0).toUpperCase() + s.slice(1)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>

                                {/* Date Range */}
                                <div className="col-md-6 col-12 mb-3" style={{ marginTop: '-10px' }}>
                                    <div className="row">
                                        <div className="col-12">
                                            <FormLabel style={{ marginBottom: '4px' }}>Date Range</FormLabel>
                                        </div>
                                        <div className="dateRangeBar mb-3">
                                            {['today', 'week', 'month', 'quarter'].map((r) => (
                                                <button
                                                    key={r}
                                                    className={`btn btn-info dateRangeBtn mx-1 mb-2${dateRange === r ? ' active' : ''}`}
                                                    onClick={() => this.updateDateRange(r)}
                                                >
                                                    {r.charAt(0).toUpperCase() + r.slice(1)}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="dateRangeBar dateRangeBarPickers">
                                            <MobileDateTimePicker
                                                value={pickerOpened ? this.state.pickerFrom : from_date}
                                                onChange={(dt) => this.setState({ pickerFrom: dt })}
                                                onOpen={() => this.setState({ pickerFrom: from_date, pickerOpened: true })}
                                                onClose={() => this.setState({ pickerOpened: false })}
                                                onAccept={(dt) => this.handleDateChange(dt.$d || dt, 'from_date')}
                                                label="From Date"
                                                renderInput={(props) => (
                                                    <TextField
                                                        {...props}
                                                        className="ml-1 mr-2 mt-2"
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton edge="end"><EventIcon /></IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            />
                                            <MobileDateTimePicker
                                                value={pickerOpened ? this.state.pickerTo : to_date}
                                                onChange={(dt) => this.setState({ pickerTo: dt })}
                                                onOpen={() => this.setState({ pickerTo: to_date, pickerOpened: true })}
                                                onClose={() => this.setState({ pickerOpened: false })}
                                                onAccept={(dt) => this.handleDateChange(dt.$d || dt, 'to_date')}
                                                label="To Date"
                                                renderInput={(props) => (
                                                    <TextField
                                                        {...props}
                                                        className="mt-2"
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton edge="end"><EventIcon /></IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right: Search + Controls */}
                        <div className="col-md-3 col-12 grid-buttons">
                            <div className="text-center">
                                <TextField
                                    id="as-grid-search"
                                    label="Current Grid Search"
                                    className="filter-search-bar mt-2"
                                    InputLabelProps={{ shrink: true }}
                                    margin="none"
                                    size="small"
                                    onChange={this.onFilterTextChange}
                                />
                                <div>
                                    <TextField
                                        sx={{ width: '100px' }}
                                        label="Page Size"
                                        className="mt-3"
                                        InputLabelProps={{ shrink: true }}
                                        type="number"
                                        margin="none"
                                        size="small"
                                        defaultValue={paginationPageSize}
                                        onChange={this.onPageSizeChanged}
                                    />
                                    <button
                                        className="btn btn-primary button-info-grid"
                                        onClick={this.applyFilters}
                                        disabled={loading}
                                    >
                                        <i className={`fas fa-sync-alt mr-1${loading ? ' fa-spin' : ''}`}></i> Refresh
                                    </button>
                                    <button
                                        className="btn btn-primary button-info-grid"
                                        onClick={this.clearFilters}
                                    >
                                        <i className="fa fa-times" aria-hidden="true"></i> Clear Filters
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Grid ── */}
                    <div className="d-flex justify-content-center gap-2 mt-1">
                        <button className="btn btn-sm btn-outline-secondary mr-2" onClick={() => { const v = document.querySelector('#as-grid .ag-body-horizontal-scroll-viewport'); if (v) v.scrollBy({ left: -200, behavior: 'smooth' }); }}>
                            <i className="fas fa-chevron-left"></i> Scroll Left
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => { const v = document.querySelector('#as-grid .ag-body-horizontal-scroll-viewport'); if (v) v.scrollBy({ left: 200, behavior: 'smooth' }); }}>
                            Scroll Right <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                    <div className="card mt-3">
                        <div
                            id="as-grid"
                            style={{ height: 'calc(100vh - 380px)', width: '100%' }}
                        >
                            <AgGridReact
                                theme={agTheme}
                                rowData={rowData}
                                columnDefs={this.columnDefs}
                                defaultColDef={this.defaultColDef}
                                pagination={true}
                                paginationPageSize={paginationPageSize}
                                rowHeight={36}
                                quickFilterText={quickFilterText}
                                onGridReady={this.onGridReady}
                                onRowClicked={this.onRowClicked}
                                context={{ openViewPopup: this.openViewPopup, openDrawer: this.openDrawer }}
                                rowStyle={{ cursor: 'pointer' }}
                                sideBar={{ toolPanels: ['columns'] }}
                                overlayLoadingTemplate={
                                    '<span class="ag-overlay-loading-center">Loading samples...</span>'
                                }
                                overlayNoRowsTemplate={
                                    '<span style="padding:20px">No samples found.</span>'
                                }
                            />
                        </div>
                    </div>
                </div>

                {/* ── Order View Popup ── */}
                <OrderViewPopup
                    show={showOrderViewPopup}
                    handleClose={() => this.setState({ showOrderViewPopup: false, orderViewDetails: null })}
                    data={orderViewDetails}
                    context={{}}
                    type="order"
                    orderDetails={orderViewDetails}
                />
                {this.state.showDrawer && this.state.drawerOrderDetails && (
                    <SubOrderDrawer
                        orderDetails={this.state.drawerOrderDetails}
                        onClose={() => this.setState({ showDrawer: false, drawerOrderDetails: null })}
                    />
                )}
            </div>
        );
    }
}

export default AllSamplesGrid;
